import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import type { RawInsertBlockAnalyticsEventProps } from '@gitbook/app-analytics';
import { createIntegrationsBlocksSet, type IntegrationsBlocksSet } from '@gitbook/app-database';
import {
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_UPDATES,
    canInsertBlockIn,
    code,
    columns,
    contentRef,
    drawing,
    expandable,
    files,
    hint,
    ifBlock,
    images,
    integration,
    list,
    MARK_BOLD,
    MARK_CODE,
    MARK_COLOR,
    type MarkColorData,
    math,
    paragraph,
    quote,
    stepper,
    table,
    tabs,
    updates,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { Icon as FAIcon } from '@gitbook/icons';
import {
    createPalette,
    filterAndSortCommands,
    type PaletteDef,
    PalettePositionWidth,
    useThemeVariants,
} from '@gitbook/react-uikit';
import {
    Block,
    BlockRange,
    type Change,
    type Document,
    type Fragment,
    isTextRangeSelection,
    Leaf,
    Mark,
    Text,
} from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { DialogRoot, ImageIcon } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiResource, readAPICache, useAssertCurrentOrganizationId } from '../../../state';
import { useSpaceSiteAdaptiveContext } from '../../AdaptiveContent';
import { createInputContentRefPalette, useContentReferenceContext } from '../../ContentReferences';
import { type ImportContentDialogProps, ImportInSpaceDialog } from '../../Imports';
import { useInsertFiles } from '../../InsertFilesDialog';
import { IntegrationBlockIcon } from '../../Integrations';
import { SpaceContext } from '../../Space';
import { useCompletionApi } from '../completion';
import { ContentEditorContext } from '../contexts';
import { useSpaceIntegrationsBlocksFromCache } from '../embed-integration';
import {
    InsertOpenAPIBlockDialog,
    type InsertOpenAPIBlockDialogProps,
} from '../openapi/InsertOpenAPIBlockDialog';
import { useReusableContentPaletteCommandsFactory } from '../reusable-content';
import { useInlineCommands } from '../useInlineCommands';
import { EMBEDS } from './embeds';
import { InsertEmbedDialog } from './InsertEmbedModal';

export type PositionType = 'replace' | 'before' | 'after';
type FocusType = 'start' | 'end' | ((change: Change, block: Block) => void);

type InsertBlockFn = (content: Block | Document, focus?: FocusType) => void;

// We use a simple open state here, we don't need a complete trigger.
type OpenState = {
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    open: () => void;
};
function useOpenState(): OpenState {
    const [isOpen, setOpen] = React.useState(false);
    return { isOpen, setOpen, open: () => setOpen(true) };
}

/**
 * Hook to return commands and modals to insert content at a specific position.
 */
export function useInsertPaletteFactory(options: {
    editor: EditorContainer;
    parent: Block | Document | Fragment;
    node: Block;
    getPosition: () => PositionType;
}): [React.ReactElement, () => PaletteDef] {
    const { editor, parent, node, getPosition } = options;
    const embedDialogTrigger = useOpenState();
    const openAPIDialogTrigger = useOpenState();
    const importDialogTrigger = useOpenState();
    const [defaultImportSource, setDefaultImportSource] =
        React.useState<ImportContentDialogProps['defaultSource']>();
    const openImport = (defaultSource?: ImportContentDialogProps['defaultSource']) => {
        setDefaultImportSource(defaultSource);
        importDialogTrigger.open();
    };
    const getSpaceIntegrationsBlocks = useSpaceIntegrationsBlocksFromCache();
    const { logAnalyticsEvent, blocks } = React.useContext(ContentEditorContext);
    const organizationId = useAssertCurrentOrganizationId();
    const { withImages, withAPIs, withDrawings, withFiles } = blocks;

    const withIfBlock = useFlag('IF_BLOCK');
    const withUpdatesBlock = useFlag('BLOCK_UPDATES');
    const variants = useThemeVariants();
    const contentRefContext = useContentReferenceContext();
    const hiveClient = useHiveClient();
    const insertFiles = useInsertFiles();
    const getInlineCommands = useInlineCommands({ editor });
    const siteAdaptiveContext = useSpaceSiteAdaptiveContext();

    /*
     * Insert block to replace current one and focus it
     */
    const insertBlock: InsertBlockFn = useEventCallback((content, focus = 'end') => {
        editor.change((change) => {
            insertBlockInBlock(change, node.key, content, getPosition(), focus);
        });
    });

    /*
     * Insert multiple blocks to replace current one and focus last
     */
    const insertBlocks = useEventCallback((contents: Array<Block>, focus: FocusType = 'end') => {
        editor.change((change) => {
            let previousBlockKey: string | undefined;

            contents.forEach((content, index) => {
                insertBlockInBlock(
                    change,
                    previousBlockKey || node.key,
                    content,
                    index === 0 ? getPosition() : 'after',
                    focus === 'start'
                        ? index === 0
                            ? 'start'
                            : null
                        : index === contents.length - 1
                          ? focus
                          : null
                );
                previousBlockKey = content.key;
            });
        });
    });

    const logInsertBlock = useEventCallback((props: RawInsertBlockAnalyticsEventProps) => {
        logAnalyticsEvent({
            type: 'edit_page_document_insert_block',
            from: 'palette',
            ...props,
        });
    });

    const getReusableContentPaletteCommands = useReusableContentPaletteCommandsFactory({
        onSelectReusableContent: (block, ref) => {
            insertBlock(block);
            logInsertBlock({
                blockType: BLOCK_REUSABLE_CONTENT,
                reusableContentId: ref.reusableContent,
            });
        },
    });

    const onInsertOpenAPIBlock: InsertOpenAPIBlockDialogProps['onInsert'] = useEventCallback(
        (blocks, sourceRef) => {
            insertBlocks(blocks, 'end');
            logInsertBlock({
                blockType: BLOCK_OPENAPI_OPERATION,
                openAPIOperations: blocks.length,
                openAPISpecSlug: sourceRef.spec,
            });
        }
    );

    const completionApi = useCompletionApi();

    const isInSpace = Boolean(React.useContext(SpaceContext));

    /*
     * Create palette to insert blocks.
     */
    const createInsertPalette = useEventCallback(() => {
        const getCommandsForIntegrationBlocks = (
            blocks: IntegrationsBlocksSet,
            filterIntegration: (integrationName: string) => boolean = () => true
        ) => {
            return Object.entries(blocks).flatMap(([integrationName, blocks]) => {
                if (!filterIntegration(integrationName)) {
                    return [];
                }

                return blocks.map((integrationBlock) => {
                    return {
                        key: `${integrationName}.${integrationBlock.id}`,
                        title: integrationBlock.title,
                        icon: <IntegrationBlockIcon block={integrationBlock} />,
                        trigger: () => {
                            // TODO: improve with a different spec for blocks
                            // to allow both unfurling or direct insertion
                            if (
                                integrationBlock.urlUnfurl &&
                                integrationBlock.urlUnfurl.length > 0
                            ) {
                                embedDialogTrigger.open();
                            } else {
                                insertBlock(
                                    integration.createIntegrationBlock(
                                        integrationName,
                                        integrationBlock.id
                                    )
                                );
                                logInsertBlock({
                                    blockType: BLOCK_INTEGRATION,
                                    integration: integrationName,
                                    integrationBlock: integrationBlock.id,
                                });
                            }
                        },
                    };
                });
            });
        };

        return createPalette({
            search: { placeholder: 'Insert block…' },
            position: {
                preferredPlacement: 'left',
                width: PalettePositionWidth.MEDIUM,
            },
            groups: [
                {
                    key: 'ai',
                    commands: [
                        completionApi && canInsertBlockIn(parent, BLOCK_PARAGRAPH)
                            ? {
                                  key: 'write-with-ai',
                                  title: 'Write with AI',
                                  icon: Icon.Magic,
                                  trigger: () => {
                                      if (!node.isEmpty) {
                                          const block = Block.create({ type: BLOCK_PARAGRAPH });
                                          insertBlock(block);
                                      }
                                      setTimeout(() => {
                                          completionApi.open({ trigger: 'commands-palette' });
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
                {
                    key: 'texts',
                    title: 'Basic blocks',
                    commands: [
                        canInsertBlockIn(parent, BLOCK_PARAGRAPH)
                            ? {
                                  key: 'paragraph',
                                  title: 'Paragraph',
                                  icon: Icon.Paragraph,
                                  trigger: () => {
                                      insertBlock(
                                          Block.create({
                                              type: BLOCK_PARAGRAPH,
                                          })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_PARAGRAPH,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_1)
                            ? {
                                  key: 'heading_1',
                                  title: 'Heading 1',
                                  icon: Icon.Heading1,
                                  keywords: ['h1', 'Title 1', 'Header 1'],
                                  trigger: () => {
                                      insertBlock(
                                          Block.create({
                                              type: BLOCK_HEADING_1,
                                          })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_HEADING_1,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_2)
                            ? {
                                  key: 'heading_2',
                                  title: 'Heading 2',
                                  icon: Icon.Heading2,
                                  keywords: ['h2', 'Title 2', 'Header 2'],
                                  trigger: () => {
                                      insertBlock(
                                          Block.create({
                                              type: BLOCK_HEADING_2,
                                          })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_HEADING_2,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_HEADING_3)
                            ? {
                                  key: 'heading_3',
                                  title: 'Heading 3',
                                  keywords: ['h3', 'Title 3', 'Header 3'],
                                  icon: Icon.Heading3,
                                  trigger: () => {
                                      insertBlock(
                                          Block.create({
                                              type: BLOCK_HEADING_3,
                                          })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_HEADING_3,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_LIST_UL)
                            ? {
                                  key: 'list_ul',
                                  title: 'Unordered list',
                                  keywords: ['ul', 'Bullet list', 'Dot list'],
                                  icon: Icon.ListUnordered,
                                  trigger: () => {
                                      insertBlock(list.createList(BLOCK_LIST_UL));
                                      logInsertBlock({
                                          blockType: BLOCK_LIST_UL,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_LIST_OL)
                            ? {
                                  key: 'list_ol',
                                  title: 'Ordered list',
                                  keywords: ['ol', 'Numbered list'],
                                  icon: Icon.ListOrdered,
                                  trigger: () => {
                                      insertBlock(list.createList(BLOCK_LIST_OL));
                                      logInsertBlock({
                                          blockType: BLOCK_LIST_OL,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_LIST_TASKS)
                            ? {
                                  key: 'list_tasks',
                                  title: 'Task list',
                                  keywords: ['Checkbox', 'Checklist', 'Tick', 'Todo list'],
                                  icon: Icon.ListTasks,
                                  trigger: () => {
                                      insertBlock(list.createList(BLOCK_LIST_TASKS));
                                      logInsertBlock({
                                          blockType: BLOCK_LIST_TASKS,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_DIVIDER)
                            ? {
                                  key: 'divider',
                                  title: 'Divider',
                                  keywords: [
                                      'Horizontal rule',
                                      'Separator',
                                      'Thematic break',
                                      'hr',
                                  ],
                                  icon: Icon.Divider,
                                  trigger: () => {
                                      insertBlock(
                                          Block.create({
                                              type: BLOCK_DIVIDER,
                                          })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_DIVIDER,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_HINT)
                            ? {
                                  key: 'hint',
                                  title: 'Hint',
                                  keywords: ['Info', 'Alert', 'Callout'],
                                  icon: Icon.InfoCircle,
                                  trigger: () => {
                                      insertBlock(hint.createHint());
                                      logInsertBlock({
                                          blockType: BLOCK_HINT,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_QUOTE)
                            ? {
                                  key: 'quote',
                                  title: 'Quote',
                                  keywords: ['Blockquote', 'Citation'],
                                  icon: Icon.Quote,
                                  trigger: () => {
                                      insertBlock(quote.create());
                                      logInsertBlock({
                                          blockType: BLOCK_QUOTE,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_CODE)
                            ? {
                                  key: 'code',
                                  title: 'Code block',
                                  keywords: ['Code', 'Syntax', 'Snippet'],
                                  icon: Icon.Code,
                                  trigger: () => {
                                      insertBlock(code.createCodeBlock('// Some code'));
                                      logInsertBlock({
                                          blockType: BLOCK_CODE,
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
                {
                    key: 'others',
                    title: 'Advanced blocks',
                    commands: [
                        withFiles && canInsertBlockIn(parent, BLOCK_FILE)
                            ? {
                                  key: 'files',
                                  title: 'Insert files…',
                                  keywords: ['Assets', 'Upload'],
                                  icon: Icon.Files,
                                  trigger: () => {
                                      insertFiles.prompt().then((selectedFiles) => {
                                          if (selectedFiles.length === 0) {
                                              editor.change((change) => change.focus());
                                              return;
                                          }

                                          const blocks: Block[] = selectedFiles
                                              .map((selectedFile) => {
                                                  return selectedFile.kind === 'file'
                                                      ? files.createFileBlock(selectedFile)
                                                      : null;
                                              })
                                              .filter(filterOutNullable);
                                          insertBlocks(blocks, 'end');
                                          logInsertBlock({
                                              blockType: BLOCK_FILE,
                                              files: blocks.length,
                                          });
                                      });
                                  },
                              }
                            : null,
                        withImages && canInsertBlockIn(parent, BLOCK_IMAGES)
                            ? {
                                  key: 'images',
                                  title: 'Insert images…',
                                  keywords: ['Photo', 'Picture', 'JPEG', 'jpg', 'png', 'GIF'],
                                  icon: Icon.Image,
                                  trigger: () => {
                                      insertFiles
                                          .prompt({
                                              title: 'Select image',
                                              accept: 'images',
                                              acceptURL: true,
                                          })
                                          .then((selectedFiles) => {
                                              if (selectedFiles.length === 0) {
                                                  editor.change((change) => change.focus());
                                                  return;
                                              }

                                              insertBlock(
                                                  Block.create({
                                                      type: BLOCK_IMAGES,
                                                      isVoid: true,
                                                      nodes: selectedFiles.map((selectedFile) => {
                                                          return images.createImageBlock(
                                                              selectedFile
                                                          );
                                                      }),
                                                  })
                                              );
                                              logInsertBlock({
                                                  blockType: BLOCK_IMAGES,
                                                  images: selectedFiles.length,
                                              });
                                          });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_EMBED)
                            ? {
                                  key: 'embed',
                                  title: 'Embed a URL…',
                                  keywords: ['music', 'video', 'audio', 'design', 'iframe'],
                                  icon: Icon.ExternalLink,
                                  trigger: () => {
                                      embedDialogTrigger.open();
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_TABLE)
                            ? {
                                  key: 'table',
                                  title: 'Table',
                                  keywords: ['Spreadsheet'],
                                  icon: Icon.Table,
                                  trigger: () => {
                                      insertBlock(table.createTable());
                                      logInsertBlock({
                                          blockType: BLOCK_TABLE,
                                          tableView: 'grid',
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_TABLE)
                            ? {
                                  key: 'table-cards',
                                  title: 'Cards',
                                  keywords: ['Grid'],
                                  icon: Icon.Cards,
                                  trigger: () => {
                                      insertBlock(
                                          table.createTable({ view: 'cards', rows: 1, columns: 1 })
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_TABLE,
                                          tableView: 'cards',
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_TABS)
                            ? {
                                  key: 'tabs',
                                  title: 'Tabs',
                                  icon: Icon.Tabs,
                                  trigger: () => {
                                      insertBlock(tabs.createTabs(), 'start');
                                      logInsertBlock({
                                          blockType: BLOCK_TABS,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_EXPANDABLE)
                            ? {
                                  key: 'expandable',
                                  title: 'Expandable',
                                  keywords: ['collapsible', 'toggle'],
                                  icon: Icon.BlockExpandable,
                                  trigger: () => {
                                      insertBlock(
                                          expandable.createExpandableBlock(),
                                          (change, block) => {
                                              change
                                                  .collapseToStartOf(
                                                      expandable.getTitleFragment(block)
                                                  )
                                                  .focus();
                                          }
                                      );
                                      logInsertBlock({
                                          blockType: BLOCK_EXPANDABLE,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_STEPPER)
                            ? {
                                  key: 'stepper',
                                  title: 'Stepper',
                                  icon: Icon.Stepper,
                                  trigger: () => {
                                      insertBlock(stepper.createStepper(), 'start');
                                      logInsertBlock({
                                          blockType: BLOCK_STEPPER,
                                      });
                                  },
                              }
                            : null,
                        withUpdatesBlock.isEnabled && canInsertBlockIn(parent, BLOCK_UPDATES)
                            ? {
                                  key: 'updates',
                                  title: 'Updates',
                                  keywords: ['changelog', 'announcements', 'news', 'updates'],
                                  icon: Icon.Megaphone,
                                  trigger: () => {
                                      insertBlock(updates.createUpdates(), 'start');
                                      logInsertBlock({
                                          blockType: BLOCK_UPDATES,
                                      });
                                  },
                              }
                            : null,
                        withDrawings && canInsertBlockIn(parent, BLOCK_DRAWING)
                            ? {
                                  key: 'drawing',
                                  title: 'Drawing',
                                  keywords: ['sketch', 'draw', 'excalidraw'],
                                  icon: Icon.EditDrawing,
                                  trigger: () => {
                                      insertBlock(drawing.createDrawingBlock());
                                      logInsertBlock({
                                          blockType: BLOCK_DRAWING,
                                      });
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_MATH)
                            ? {
                                  key: 'math',
                                  title: 'Math & TeX',
                                  keywords: ['Formula'],
                                  icon: Icon.MathTeX,
                                  trigger: () => {
                                      insertBlock(math.createMathBlock());
                                      logInsertBlock({
                                          blockType: BLOCK_MATH,
                                      });
                                  },
                              }
                            : null,
                        withAPIs && canInsertBlockIn(parent, BLOCK_OPENAPI)
                            ? {
                                  key: 'openapi',
                                  title: 'OpenAPI',
                                  icon: Icon.OpenAPI,
                                  keywords: ['api', 'swagger', 'openapi', 'rest'],
                                  trigger: () => {
                                      openAPIDialogTrigger.open();
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_CONTENT_REF)
                            ? {
                                  key: 'content-ref',
                                  title: 'Page link',
                                  icon: Icon.AddLink,
                                  keywords: ['references'],
                                  trigger: () => {
                                      return createInputContentRefPalette(
                                          (ref) => {
                                              insertBlock(contentRef.createContentRefBlock(ref));
                                              logInsertBlock({
                                                  blockType: BLOCK_CONTENT_REF,
                                                  contentRefKind: ref.kind,
                                              });
                                          },
                                          contentRefContext,
                                          hiveClient,
                                          {
                                              search: {
                                                  placeholder: 'Search in spaces and collections…',
                                              },
                                              accept: ['page', 'space', 'collection'],
                                          }
                                      );
                                  },
                              }
                            : null,
                        canInsertBlockIn(parent, BLOCK_COLUMNS)
                            ? {
                                  key: 'columns',
                                  title: 'Columns',
                                  keywords: ['Layout', 'Columns'],
                                  icon: Icon.Columns,
                                  trigger: () => {
                                      insertBlock(columns.createColumnsBlock());
                                      logInsertBlock({
                                          blockType: BLOCK_COLUMNS,
                                      });
                                  },
                              }
                            : null,
                        withIfBlock.isEnabled &&
                        siteAdaptiveContext.site &&
                        canInsertBlockIn(parent, BLOCK_IF)
                            ? {
                                  key: 'if',
                                  title: 'Conditional content',
                                  upgrade: siteAdaptiveContext.isUpgradeRequired,
                                  badge: siteAdaptiveContext.isUpgradeRequired
                                      ? {
                                            kind: 'upgrade',
                                            label: 'Upgrade',
                                        }
                                      : undefined,
                                  keywords: ['conditional', 'expression', 'if'],
                                  icon: Icon.Condition,
                                  trigger: () => {
                                      if (
                                          siteAdaptiveContext.isUpgradeRequired ||
                                          !siteAdaptiveContext.site?.adaptiveContent?.enabled
                                      ) {
                                          return siteAdaptiveContext.openFeatureIntroDialog(
                                              'if-block'
                                          );
                                      }
                                      insertBlock(ifBlock.createIfBlock());
                                      logInsertBlock({
                                          blockType: BLOCK_IF,
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },

                //
                // Reusable content in the same space.
                //
                canInsertBlockIn(parent, BLOCK_REUSABLE_CONTENT) &&
                getReusableContentPaletteCommands
                    ? {
                          key: 'reusable-content',
                          title: 'Reusable content',
                          commands: getReusableContentPaletteCommands(),
                      }
                    : null,

                //
                // Blocks integrations installed on the org
                //
                canInsertBlockIn(parent, BLOCK_INTEGRATION)
                    ? {
                          key: 'integrations',
                          title: 'Integrations',
                          commands: () => {
                              const spaceIntegrationBlocks = getSpaceIntegrationsBlocks();
                              return getCommandsForIntegrationBlocks(spaceIntegrationBlocks);
                          },
                      }
                    : null,

                //
                // Suggest blocks integrations
                //
                canInsertBlockIn(parent, BLOCK_INTEGRATION)
                    ? {
                          key: 'integrations',
                          title: 'Discover integrations',
                          commands: (palette) => {
                              const { text } = palette.query;

                              // All blocks integrations
                              const data = readAPICache(
                                  apiResource(
                                      'listOrganizationIntegrations',
                                      { organizationId, blocks: true, limit: 100 },
                                      { nullOnError: true }
                                  )
                              );
                              const suggestedIntegrationBlocks = data
                                  ? createIntegrationsBlocksSet(data.items)
                                  : {};

                              if (!Object.keys(suggestedIntegrationBlocks).length) {
                                  return [];
                              }

                              // Blocks integration installed/configured on the current space
                              const spaceIntegrationBlocks = getSpaceIntegrationsBlocks();

                              const integrationsBlocksCommands = getCommandsForIntegrationBlocks(
                                  suggestedIntegrationBlocks,
                                  (suggestedIntegrationName) => {
                                      // Don't show duplicate for already installed integrations
                                      return !spaceIntegrationBlocks[suggestedIntegrationName];
                                  }
                              );

                              if (text.length > 0) {
                                  const { commands } = filterAndSortCommands(
                                      text,
                                      integrationsBlocksCommands
                                  );
                                  return commands;
                              }

                              return integrationsBlocksCommands.slice(0, 10);
                          },
                      }
                    : null,

                //
                // Quickstarts
                //
                {
                    key: 'quickstarts',
                    title: 'Quickstarts',
                    commands: quickstarts
                        .map((quickstart) => {
                            const supported = quickstart.blocks.every((block) =>
                                canInsertBlockIn(parent, block)
                            );
                            if (!supported) {
                                return null;
                            }
                            return {
                                key: quickstart.key,
                                title: quickstart.title,
                                subtitle: quickstart.description,
                                icon: quickstart.icon,
                                trigger: () => {
                                    insertBlocks(quickstart.generate(), 'start');
                                    logAnalyticsEvent({
                                        type: 'edit_page_document_insert_quickstart',
                                        quickstart: quickstart.key,
                                    });
                                },
                            };
                        })
                        .filter(filterOutNullable),
                },

                //
                // Embeds
                //
                canInsertBlockIn(parent, BLOCK_EMBED)
                    ? {
                          key: 'embeds',
                          title: 'Embeds',
                          commands: EMBEDS.map((embed) => ({
                              key: embed.key,
                              title: embed.title,
                              icon: (
                                  <ImageIcon
                                      src={
                                          (variants.color === 'dark' ? embed.iconDark : null) ||
                                          embed.icon
                                      }
                                  />
                              ),
                              trigger: () => {
                                  embedDialogTrigger.open();
                              },
                          })),
                      }
                    : null,
                {
                    key: 'inline-controls',
                    title: 'Inline',
                    commands: () => {
                        return getPosition() === 'replace' && node.isEmpty
                            ? getInlineCommands()
                            : [];
                    },
                },
                isInSpace
                    ? {
                          key: 'import',
                          title: 'Import',
                          commands: [
                              {
                                  key: 'import',
                                  title: 'Import',
                                  icon: Icon.FileDownload,
                                  trigger: () => {
                                      openImport();
                                  },
                              },
                              {
                                  key: 'import-website',
                                  title: 'Website',
                                  keywords: ['import', 'web', 'url', 'link'],
                                  icon: Icon.Globe,
                                  trigger: () => {
                                      openImport('website');
                                  },
                              },
                              {
                                  key: 'import-markdown',
                                  title: 'Markdown',
                                  keywords: ['import', 'markdown', 'md', 'mdx'],
                                  icon: <FAIcon icon="markdown" />,
                                  trigger: () => {
                                      openImport('file');
                                  },
                              },
                              {
                                  key: 'import-html',
                                  title: 'HTML',
                                  keywords: ['import', 'html'],
                                  icon: Icon.FileCode,
                                  trigger: () => {
                                      openImport('file');
                                  },
                              },
                              {
                                  key: 'import-zip',
                                  title: 'ZIP',
                                  keywords: ['import', 'zip', 'archive'],
                                  icon: Icon.FileZIP,
                                  trigger: () => {
                                      openImport('file');
                                  },
                              },
                          ],
                      }
                    : null,
            ].filter(filterOutNullable),
        });
    });

    return [
        <>
            {isInSpace ? (
                <ImportInSpaceDialogMemo
                    trigger={importDialogTrigger}
                    defaultSource={defaultImportSource}
                />
            ) : null}
            <InsertEmbedDialogMemo trigger={embedDialogTrigger} insertBlock={insertBlock} />
            <InsertOpenAPIBlockDialogMemo
                trigger={openAPIDialogTrigger}
                onInsert={onInsertOpenAPIBlock}
            />
        </>,
        createInsertPalette,
    ];
}

const ImportInSpaceDialogMemo = React.memo(function ImportInSpaceMemoDialog(props: {
    defaultSource: ImportContentDialogProps['defaultSource'];
    trigger: OpenState;
}) {
    const { trigger, defaultSource } = props;
    return (
        <DialogRoot isOpen={trigger.isOpen} onOpenChange={trigger.setOpen}>
            <ImportInSpaceDialog defaultSource={defaultSource} />
        </DialogRoot>
    );
});

const InsertEmbedDialogMemo = React.memo(function InsertEmbedDialogMemo(props: {
    trigger: OpenState;
    insertBlock: InsertBlockFn;
}) {
    const { trigger, insertBlock } = props;
    return (
        <DialogRoot isOpen={trigger.isOpen} onOpenChange={trigger.setOpen}>
            <InsertEmbedDialog onInsert={insertBlock} />
        </DialogRoot>
    );
});

const InsertOpenAPIBlockDialogMemo = React.memo(function InsertOpenAPIBlockDialogMemo(
    props: Pick<InsertOpenAPIBlockDialogProps, 'onInsert'> & {
        trigger: OpenState;
    }
) {
    const { trigger, onInsert } = props;
    return (
        <DialogRoot isOpen={trigger.isOpen} onOpenChange={trigger.setOpen}>
            <InsertOpenAPIBlockDialog onInsert={onInsert} />
        </DialogRoot>
    );
});

/**
 * A method to insert a block into another one.
 * @param editor Slate editor
 * @param nodeKey Key of the block to replace or insert in
 * @param content Block, Document, or Fragment to insert
 * @param position Position of where to insert the `content`
 * @param focus Where to focus once the block has been inserted
 */
export function insertBlockInBlock(
    change: Change,
    nodeKey: string,
    content: Block | Document | Fragment,
    position: PositionType,
    focus: FocusType | null
) {
    const parentBlock = change.value.document.getParent(nodeKey);
    if (!parentBlock) {
        return;
    }
    const nodeIndex = parentBlock.getChildIndex(nodeKey);
    if (nodeIndex < 0) {
        return;
    }

    if (position === 'replace') {
        if (content.object === 'block') {
            change.replaceNodeByKey(nodeKey, content);
        } else {
            change.replaceWithDocumentByKey(nodeKey, content);
        }
    } else {
        const insertIndex = position === 'after' ? nodeIndex + 1 : nodeIndex;

        if (content.object === 'block') {
            change.insertNodeByKey(parentBlock.key, insertIndex, content);
        } else {
            change.insertDocumentByKey(parentBlock.key, insertIndex, content);
        }
    }

    const newBlock = change.value.document.getNode(
        content.object === 'block' ? content.key : content.nodes.first()!.key
    ) as Block | null;
    if (newBlock && focus) {
        if (isTextRangeSelection(change.value.selection)) {
            if (focus === 'start') {
                change.collapseToStartOf(newBlock).focus();
            } else if (focus === 'end') {
                change.collapseToEndOf(newBlock).focus();
            } else {
                focus(change, newBlock);
            }
        } else {
            change.selectBlock(
                BlockRange.create({ anchorBlock: newBlock.key, focusBlock: newBlock.key })
            );
        }
    }
}

const quickstarts: Array<{
    key: string;
    title: string;
    description: string;
    blocks: string[];
    icon: SVGIconComponent;
    generate: () => Block[];
}> = [
    {
        key: 'api',
        title: 'API reference',
        description: 'With table, tabs and code',
        icon: Icon.OpenAPI,
        blocks: [BLOCK_TABLE, BLOCK_TABS, BLOCK_CODE],
        generate: () => {
            return [
                Block.create({
                    type: BLOCK_HEADING_1,
                    nodes: [
                        Text.create({
                            text: 'Create a new user',
                        }),
                    ],
                }),
                paragraph.createParagraph([
                    Text.create({
                        leaves: [
                            Leaf.create({
                                text: 'POST',
                                marks: [
                                    Mark.create({ type: MARK_CODE }),
                                    Mark.create({
                                        type: MARK_COLOR,
                                        data: {
                                            text: 'green',
                                            background: 'default',
                                        } as MarkColorData,
                                    }),
                                ],
                            }),
                            Leaf.create({
                                text: ' ',
                            }),
                            Leaf.create({
                                text: '/users',
                                marks: [Mark.create({ type: MARK_CODE })],
                            }),
                        ],
                    }),
                ]),
                paragraph.createParagraph([
                    Text.create({
                        text: '<Description of the endpoint>',
                    }),
                ]),
                paragraph.createParagraph([
                    Text.create({
                        text: 'Headers',
                        marks: [Mark.create({ type: MARK_BOLD })],
                    }),
                ]),
                table.createTableFromData(
                    [{ title: 'Name' }, { title: 'Value' }],
                    [
                        [
                            { value: 'Content-Type' },
                            {
                                value: [
                                    paragraph.createParagraph([
                                        Text.create({
                                            text: 'application/json',
                                            marks: [Mark.create({ type: MARK_CODE })],
                                        }),
                                    ]),
                                ],
                            },
                        ],
                        [
                            { value: 'Authorization' },
                            {
                                value: [
                                    paragraph.createParagraph([
                                        Text.create({
                                            text: 'Bearer <token>',
                                            marks: [Mark.create({ type: MARK_CODE })],
                                        }),
                                    ]),
                                ],
                            },
                        ],
                    ]
                ),
                paragraph.createParagraph([
                    Text.create({
                        text: 'Body',
                        marks: [Mark.create({ type: MARK_BOLD })],
                    }),
                ]),
                table.createTableFromData(
                    [{ title: 'Name' }, { title: 'Type' }, { title: 'Description' }],
                    [
                        [
                            {
                                value: [
                                    paragraph.createParagraph([
                                        Text.create({
                                            text: 'name',
                                            marks: [Mark.create({ type: MARK_CODE })],
                                        }),
                                    ]),
                                ],
                            },
                            {
                                value: 'string',
                            },
                            { value: 'Name of the user' },
                        ],
                        [
                            {
                                value: [
                                    paragraph.createParagraph([
                                        Text.create({
                                            text: 'age',
                                            marks: [Mark.create({ type: MARK_CODE })],
                                        }),
                                    ]),
                                ],
                            },
                            { value: 'number' },
                            { value: 'Age of the user' },
                        ],
                    ]
                ),
                paragraph.createParagraph([
                    Text.create({
                        text: 'Response',
                        marks: [Mark.create({ type: MARK_BOLD })],
                    }),
                ]),
                tabs.createTabs([
                    tabs.createTabsItem('200', [
                        code.createCodeBlock(
                            JSON.stringify({ id: 1, name: 'John', age: 30 }, null, 2),
                            {
                                syntax: 'json',
                            }
                        ),
                    ]),
                    tabs.createTabsItem('400', [
                        code.createCodeBlock(
                            JSON.stringify({ error: 'Invalid request' }, null, 2),
                            {
                                syntax: 'json',
                            }
                        ),
                    ]),
                ]),
            ];
        },
    },
    {
        key: 'codeblock-tabs',
        title: 'Multi-syntax code',
        description: 'With tabs and code blocks',
        icon: Icon.Code,
        blocks: [BLOCK_TABS],
        generate: () => {
            return [
                tabs.createTabs([
                    tabs.createTabsItem('JavaScript', [
                        code.createCodeBlock(
                            'const message = "hello world";\nconsole.log(message);',
                            {
                                syntax: 'javascript',
                            }
                        ),
                    ]),
                    tabs.createTabsItem('Python', [
                        code.createCodeBlock('message = "hello world"\nprint(message)', {
                            syntax: 'python',
                        }),
                    ]),
                    tabs.createTabsItem('Ruby', [
                        code.createCodeBlock('message = "hello world"\nputs message', {
                            syntax: 'ruby',
                        }),
                    ]),
                ]),
            ];
        },
    },
];
